import {Button, Divider, Flex} from 'antd';
import {Layout, OrgChart} from 'd3-org-chart';
import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import ReactDOMServer from 'react-dom/server';

import {ReactComponent as SmallCom} from '../../../assets/smallComOrgIcon.svg';
import {ReactComponent as SmallLeft} from '../../../assets/smallLeftOrg.svg';
import {ReactComponent as SmallRight} from '../../../assets/smallRightOrg.svg';
import Year from '../../../shared/Year';
import {convertPxToRem} from '../../../utils/commonFunctions';
import PilComp from '../../DisplayComponent/PilComp';
import sumTableValue from '../../Table/Utils/sumTableValue';

import CustomExpandButton from './CustomExpandButton';
import CustomNodeContent from './CustomNodeContent';

const styles = {
  orgChart: {
    backgroundImage: 'radial-gradient(#BFBFBF 2%, transparent 10%)',
    backgroundColor: '#FFF',
    backgroundSize: '20px 20px',
    height: '100%'
  }
};

const OrgChartComponent: React.FC<{
  data: any;
  isHorizontal?: boolean;
  chartType: string;
  showLegend?: boolean;
  setSelectedYear: React.Dispatch<React.SetStateAction<string>>;
}> = ({data, isHorizontal = true, setSelectedYear, chartType}) => {
  const [mainData, setMainData] = useState<any[]>([]);
  const [layout, setLayout] = useState<Layout>(isHorizontal ? 'top' : 'left');
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [colCompData, setColCompData] = useState<any | undefined>(undefined);
  const [chartContainer] = useState(new OrgChart());
  const d3Container = useRef(null);

  useEffect(() => {
    setMainData(data);
  }, [data]);

  useLayoutEffect(() => {
    if (mainData.length > 0 && d3Container.current) {
      setColCompData(() => {
        const parent = mainData.find((item: any) => {
          return item?.parentId === '';
        });

        const sub = mainData
          .filter((item: any) => {
            return item?.parentId === parent?.id;
          })
          .map((item) => {
            if (['Facilities', 'Franchise'].includes(item.name)) {
              return item;
            } else {
              return {
                ...item,
                direct: item?._directSubordinates,
                _directSubordinates:
                  item._totalSubordinates - item._directSubordinatesPaging
              };
            }
          });

        const parentName = parent.name.split(' ');

        return {
          parent: {
            ...parent,
            shortName: `${parentName?.[0]?.[0]}${parentName?.[1]?.[0] ?? ''}`,
            _directSubordinates: sumTableValue(
              sub,
              '_directSubordinates',
              false
            )
          },
          sub
        };
      });
      chartContainer
        .container(d3Container.current)
        .compact(false)
        .layout(layout)
        // .nodeButtonWidth(() => {
        //   return 16;
        // })
        // .nodeButtonHeight(() => {
        //   return 16;
        // })
        // .nodeButtonX((d: any) => {
        //   if (d.depth === 0) return 440;

        //   return d?.data?.name.length * 16 + d?.data?.name.length;
        // })
        // .nodeButtonY((d) => {
        //   if (d.depth === 0) return 60;

        //   return 20;
        // })
        // .nodeWidth((d) => 300)
        // .onNodeClick((d) => {
        //   toggleDetailsCard(d);
        // })
        .buttonContent((node: any) => {
          return ReactDOMServer.renderToStaticMarkup(
            <CustomExpandButton {...node.node} />
          );
        })
        .nodeWidth((d: any) => {
          const isGreater =
            d?.data?.name?.length * 10 > 500 ? d?.data?.name?.length * 10 : 500;
          if (d.depth === 0) return isGreater;

          return d.children
            ? d?.data?.name.length * 15 + 32
            : d?.data?.name.length * 14 + 32;
        })
        .nodeHeight((d) => {
          if (d.depth === 0) return 150;

          return 75;
        })
        .nodeContent((d: any) => {
          return ReactDOMServer.renderToStaticMarkup(
            <CustomNodeContent {...d} />
          );
        })
        .siblingsMargin(() => 150)
        .data(mainData)
        .render()
        .fit();
    }
  }, [mainData, layout]);

  const finalChildrenCount = colCompData?.parent._directSubordinates;
  const exportNodeCount = mainData.length;
  const deviceScale = Math.max(6, Math.ceil((window.devicePixelRatio || 1) * 4));
  const densityScale = Math.max(6, Math.ceil(Math.sqrt(Math.max(exportNodeCount, 1))));
  const adjustedScale =
    exportNodeCount > 600 ? 7 : exportNodeCount > 300 ? 8 : 10;
  const imageScale = Math.min(12, Math.max(deviceScale, densityScale, adjustedScale));

  return (
    <div className="main-container">
      <Flex className="button-container">
        <Flex gap={10}>
          <Button
            type="default"
            onClick={() => {
              return chartContainer.fit();
            }}
            disabled={mainData.length === 0}
          >
            Fit to screen
          </Button>
          <Button
            type="default"
            onClick={() => {
              chartContainer.expandAll();
              chartContainer.fit();
            }}
            disabled={mainData.length === 0}
          >
            Expand All
          </Button>
          <Button
            type="default"
            onClick={() => {
              chartContainer.collapseAll();
              chartContainer.fit();
            }}
            disabled={mainData.length === 0}
          >
            Collapse All
          </Button>
          <Button
            type="default"
            onClick={() => {
              chartContainer.exportImg({
                full: true,
                scale: imageScale,
                backgroundColor: '#FFFFFF'
              });
            }}
            disabled={mainData.length === 0}
          >
            Download Full Image
          </Button>
          <Button
            type="default"
            onClick={() => {
              if (layout === 'top') {
                setLayout('left');
              } else {
                setLayout('top');
              }
            }}
            disabled={mainData.length === 0}
          >
            {layout === 'top' ? 'Vertical' : 'Horizontal'}
          </Button>
        </Flex>
        <Flex>
          <Year
            setSelectedYear={setSelectedYear}
            isESG
            noPadding
            noBottomMargin
            colSpan={24}
          />
        </Flex>
      </Flex>
      {chartType === 'orgHierarchy' && (
        <Flex
          style={{
            width: 'fit-content',
            position: 'absolute',
            backgroundColor: 'white',
            top: '10%',
            left: '2%',
            borderRadius: '8px',
            boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.16)',
            transition: collapsed ? '5s' : '5s'
          }}
          vertical
        >
          <Button
            icon={collapsed ? <SmallRight /> : <SmallLeft />}
            onClick={() => {
              setCollapsed(!collapsed);
            }}
            shape="circle"
            style={{
              backgroundColor: '#E1F3D9',
              border: 'none',
              alignSelf: 'flex-end',
              position: 'absolute',
              top: '5%',
              right: convertPxToRem(-15)
            }}
          />
          <Flex
            style={{padding: '13px 34px 13px 14px', gap: 5}}
            align="center"
            justify="center"
          >
            <SmallCom />
            <div
              style={{
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '120%'
              }}
            >
              {collapsed
                ? colCompData?.parent?.shortName ?? 'C'
                : colCompData?.parent?.name ?? 'Company'}
            </div>
            <PilComp value={finalChildrenCount ?? 0} />
          </Flex>
          <Divider style={{margin: '0px'}} />
          <Flex vertical style={{padding: '14px 14px', gap: 5}}>
            {colCompData?.sub.map((item: any, index: number) => {
              return (
                <Flex
                  key={index}
                  align="center"
                  justify={collapsed ? 'space-around' : 'space-between'}
                >
                  <div
                    style={{
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: '120%'
                    }}
                  >
                    {collapsed ? item?.name?.[0] : item?.name ?? 'Company'}
                  </div>
                  <PilComp
                    value={item?._directSubordinates ?? 0}
                    color="#E1F3D9"
                  />
                </Flex>
              );
            })}
          </Flex>
        </Flex>
      )}
      <div style={styles.orgChart} ref={d3Container}></div>
    </div>
  );
};

export default OrgChartComponent;
